import { Component, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { versions } from 'src/app/versions';
import { projectUuidFromPath } from '../request-access/request-access.component';

@Component({
  selector: 'app-user-login',
  templateUrl: './user-login.component.html',
  styleUrls: ['./user-login.component.scss'],
  standalone: false,
})
export class UserLoginComponent implements OnInit {
  // Signals, because the app does not repaint after async work on its own: a plain
  // field set after `await login()` would leave the spinner or a missing error on screen.
  readonly isLoading = signal(false);
  readonly loginError = signal<string | null>(null);
  readonly showPassword = signal(false);

  readonly versions = versions;

  /** The in-app page the visitor was sent to login from (the auth guard puts it in the fragment). */
  returnPath = '';
  /** Set when that page belongs to a project, so someone without access can ask for it. */
  requestAccessProject: string | null = null;

  form = new FormGroup({
    username: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', Validators.required),
  });

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    const fragment = this.route.snapshot.fragment ?? '';
    // Only in-app pages: never follow an arbitrary URL from the address bar.
    this.returnPath = /^\/main(\/|$)/.test(fragment) ? fragment : '';
    this.requestAccessProject = projectUuidFromPath(this.returnPath);

    if (this.authService.getAccessToken() && !this.authService.isTokenExpired()) {
      this.goToApp();
    }
  }

  private goToApp(): void {
    if (this.returnPath) this.router.navigateByUrl(this.returnPath);
    else this.router.navigate(['/main']);
  }

  invalid(name: 'username' | 'password'): boolean {
    const control = this.form.get(name);
    return !!control && control.touched && control.invalid;
  }

  async login(): Promise<void> {
    this.form.markAllAsTouched();
    this.loginError.set(null);
    if (this.form.invalid || this.isLoading()) return;

    this.isLoading.set(true);
    try {
      const { username, password } = this.form.getRawValue();
      const authenticated = await lastValueFrom(this.authService.login(username?.trim(), password));
      if (authenticated) {
        this.goToApp();
      } else {
        this.loginError.set('Invalid email or password. Please try again.');
      }
    } catch (error) {
      console.error(error);
      this.loginError.set('Something went wrong. Please try again.');
    } finally {
      this.isLoading.set(false);
    }
  }
}
